/**
 * Classifies a job title into a role + level bucket for the Jobs page's
 * "Job title" grouping. Role is TPM vs. Product Manager, crossed with
 * whether the title is a people-manager/director role; level is the
 * Staff/Principal/Senior seniority word found in the title, if any.
 */

const ROLE_LABELS = [
  "Manager or Director, Technical Program Manager (TPM)",
  "Manager or Director, Product Manager",
  "Technical Program Manager (TPM)",
  "Product Manager",
] as const;

const LEVELS = ["Staff", "Principal", "Senior"] as const;

export const OTHER_ROLE_GROUP = "Other roles";

/** All possible "Job title" group labels, in display order (used to sort groups). */
export const TITLE_GROUP_ORDER: string[] = [
  ...ROLE_LABELS.flatMap((role) => [...LEVELS.map((level) => `${level} ${role}`), role]),
  OTHER_ROLE_GROUP,
];

export function classifyJobTitle(title: string): string {
  const t = title.toLowerCase();

  const isPM = /product manag/.test(t);
  const isTPM =
    !isPM &&
    (/technical program manag/.test(t) ||
      /engineering program manag/.test(t) ||
      /\btpm\b/.test(t) ||
      /\bepm\b/.test(t) ||
      /\bprogram manag/.test(t));

  if (!isPM && !isTPM) return OTHER_ROLE_GROUP;

  // Strip the spelled-out role phrase so a leftover "manager"/"director" means
  // the title carries an extra people-leadership word (e.g. "Manager, Engineering
  // Program Manager" or "Director, Technical Program Management").
  const stripped = t
    .replace(/technical program manag\w*/g, " ")
    .replace(/engineering program manag\w*/g, " ")
    .replace(/product manag\w*/g, " ")
    .replace(/\bprogram manag\w*/g, " ");
  const isLead = /\bdirector\b/.test(stripped) || /\bmanager\b/.test(stripped);

  const roleLabel = isPM
    ? ROLE_LABELS[isLead ? 1 : 3]
    : ROLE_LABELS[isLead ? 0 : 2];

  const level = /\bprincipal\b/.test(t)
    ? "Principal"
    : /\bstaff\b/.test(t)
      ? "Staff"
      : /\bsenior\b/.test(t) || /\bsr\b/.test(t)
        ? "Senior"
        : "";

  return level ? `${level} ${roleLabel}` : roleLabel;
}
